// Onboarding-schermen — web-port 1-op-1 van app/onboarding/{intro,mode,partner,profile,type,preferences}.tsx
// Ingevulde toestand volgens de Ghost Driver showcase-seed (auto_demo.py).
import React, { useEffect, useState } from 'react';
import Icon from '../../Icon';
import { C } from '../../theme';

const sh = { card: '0 6px 16px rgba(15,27,45,0.08)', soft: '0 2px 8px rgba(15,27,45,0.05)' };

// ── Gedeelde app-bouwblokken (Button, OnboardingHeader) ─────────
function OnboardingHeader({ eyebrow, title, subtitle }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {eyebrow ? (
        <div style={{ color: C.gold, fontSize: 11, fontWeight: 800, letterSpacing: 1.4, textTransform: 'uppercase' }}>{eyebrow}</div>
      ) : null}
      <div style={{ color: C.navy, fontSize: 24, fontWeight: 900, letterSpacing: -0.5, lineHeight: '30px' }}>{title}</div>
      {subtitle ? <div style={{ color: C.inkMuted, fontSize: 14, lineHeight: '20px' }}>{subtitle}</div> : null}
    </div>
  );
}

function AppButton({ label, variant = 'gold', testid }) {
  const gold = variant === 'gold';
  const primary = variant === 'primary';
  return (
    <div
      data-testid={testid}
      style={{
        height: 52, borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: gold ? C.gold : primary ? C.navy : 'transparent',
        boxShadow: gold || primary ? sh.card : 'none',
      }}
    >
      <span style={{
        color: gold ? C.navy : primary ? C.white : C.navy,
        fontSize: 15.5, fontWeight: 800, letterSpacing: 0.1,
      }}>{label}</span>
    </div>
  );
}

const onbRoot = { minHeight: '100%', background: C.bgSoft, paddingBottom: 60 };
const onbScroll = { padding: '46px 16px 60px', display: 'flex', flexDirection: 'column', gap: 14 };

// ═══════════════════════════════════════════════════════════════
// intro.tsx — "WELKOM BIJ IMMO MATCH" + 4 pijlers + woonreis
// ═══════════════════════════════════════════════════════════════
function Pillar({ icon, title, desc }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', background: C.white, padding: 14, borderRadius: 14, border: `1px solid ${C.line}`, boxShadow: sh.soft }}>
      <div style={{ width: 34, height: 34, borderRadius: 17, background: 'rgba(196,138,58,0.15)', border: '1px solid rgba(196,138,58,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon name={icon} size={16} color={C.gold} />
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <div style={{ color: C.navy, fontSize: 14, fontWeight: 800, letterSpacing: -0.2 }}>{title}</div>
        <div style={{ color: C.inkMuted, fontSize: 12.5, lineHeight: '17px' }}>{desc}</div>
      </div>
    </div>
  );
}

function JourneyStep({ n, label }) {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
      <div style={{ width: 28, height: 28, borderRadius: 14, background: C.gold, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: C.navy, fontSize: 13, fontWeight: 900 }}>{n}</span>
      </div>
      <div style={{ color: C.white, fontSize: 10.5, fontWeight: 700, textAlign: 'center', lineHeight: '13px' }}>{label}</div>
    </div>
  );
}

export function OnbIntro({ mode }) {
  const isCouple = mode === 'couple';
  return (
    <div style={onbRoot} data-testid="onb-intro">
      <div style={{ padding: '0 16px 60px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Logo bovenaan — gecentreerd op navy blok */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '46px 0 24px', background: '#0e2340',
          borderBottomLeftRadius: 24, borderBottomRightRadius: 24,
          margin: '0 -16px',
        }}>
          <img src="/logo_official.png" alt="IMMO MATCH" style={{ width: 200, height: 200, objectFit: 'contain' }} />
        </div>

        <div style={{ textAlign: 'center', padding: '0 8px' }}>
          <div style={{ color: C.gold, fontSize: 11, fontWeight: 900, letterSpacing: 1.4 }}>WELKOM BIJ IMMO MATCH</div>
          <div style={{ color: C.navy, fontSize: 24, fontWeight: 900, letterSpacing: -0.5, marginTop: 8, lineHeight: '30px' }}>
            {isCouple ? <>Jullie persoonlijke<br />Wooncoach.</> : <>Jouw persoonlijke<br />Wooncoach.</>}
          </div>
          <div style={{ color: C.inkMuted, fontSize: 14, lineHeight: '20px', marginTop: 10, marginBottom: 8, maxWidth: 340, marginLeft: 'auto', marginRight: 'auto' }}>
            {isCouple
              ? 'IMMO MATCH is geen vergelijkingssite. Het is een rustige, deskundige begeleiding die jullie meeneemt van eerste swipe tot in jullie nieuwe thuis.'
              : 'IMMO MATCH is geen vergelijkingssite. Het is een rustige, deskundige begeleiding die je meeneemt van eerste swipe tot in je nieuwe thuis.'}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 6 }}>
          <Pillar icon="heart" title={isCouple ? 'Woningen die écht bij jullie passen' : 'Woningen die écht bij je passen'} desc={isCouple ? 'Geen eindeloos scrollen. IMMO MATCH leert wat jullie mooi vinden en verfijnt bij elke swipe.' : 'Geen eindeloos scrollen. IMMO MATCH leert wat jij mooi vindt en verfijnt bij elke swipe.'} />
          <Pillar icon="users" title="Samen zoeken? Uniek in België." desc={isCouple ? "Alleen matches als jullie beiden ja zeggen. Ook agenda's en bezoeken worden gecombineerd." : 'Zoek je later samen met een partner? Alleen matches als beiden ja zeggen, én gedeelde agenda voor bezoeken.'} />
          <Pillar icon="compass" title={isCouple ? 'De Wooncoach denkt met jullie mee' : 'De Wooncoach denkt met je mee'} desc="Afstanden in km, buurt-inzichten, iceberg-progressie en concrete inzichten wanneer het telt." />
          <Pillar icon="award" title="Van eerste swipe tot verhuisdoos" desc="Financiering, verzekering, notaris, verhuizen en renovatie — allemaal via één woonassistent." />
        </div>

        <div style={{ background: '#0A1A2C', padding: 16, borderRadius: 14, marginTop: 4, display: 'flex', flexDirection: 'column', gap: 10, border: '1px solid rgba(196,138,58,0.3)' }}>
          <div style={{ color: C.gold, fontSize: 10, fontWeight: 900, letterSpacing: 1.5 }}>{isCouple ? 'ZO BEGELEIDEN WE JULLIE' : 'ZO BEGELEIDEN WE JE'}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <JourneyStep n={1} label={isCouple ? 'Wat zoeken jullie?' : 'Wat zoek je?'} />
            <JourneyStep n={2} label="Swipen & matchen" />
            <JourneyStep n={3} label="Bezoeken" />
            <JourneyStep n={4} label="Beslissen & wonen" />
          </div>
        </div>

        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <AppButton label="Laten we beginnen" variant="gold" testid="intro-start" />
          <div style={{ color: C.inkFaint, fontSize: 12, fontStyle: 'italic', textAlign: 'center' }}>Duurt ongeveer 2 minuten om in te stellen.</div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// mode.tsx — "STAP 1 · WIE ZOEKT?" — Ik zoek alleen / Wij zoeken onze thuis
// ═══════════════════════════════════════════════════════════════
function ModeCard({ selected, icon, title, desc, badge, testid }) {
  return (
    <div
      data-testid={testid}
      style={{
        background: selected ? 'rgba(196,138,58,0.05)' : C.white, padding: 18, borderRadius: 14,
        border: `1.5px solid ${selected ? C.gold : C.line}`, display: 'flex', flexDirection: 'column', gap: 8,
        position: 'relative', boxShadow: selected ? sh.card : sh.soft,
      }}
    >
      {badge ? (
        <div style={{ position: 'absolute', top: -10, left: 14, background: C.gold, padding: '4px 10px', borderRadius: 999 }}>
          <span style={{ color: C.navy, fontSize: 9.5, fontWeight: 900, letterSpacing: 1 }}>{badge}</span>
        </div>
      ) : null}
      <div style={{
        width: 46, height: 46, borderRadius: 23,
        background: selected ? C.gold : 'rgba(16,42,67,0.08)',
        border: `1px solid ${selected ? C.gold : C.line}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon name={icon} size={22} color={selected ? C.white : C.navy} />
      </div>
      <div style={{ color: C.navy, fontSize: 17, fontWeight: 900, letterSpacing: -0.3, marginTop: 6 }}>{title}</div>
      <div style={{ color: C.inkMuted, fontSize: 13, lineHeight: '18px' }}>{desc}</div>
      {selected ? (
        <div style={{ position: 'absolute', top: 14, right: 14, width: 24, height: 24, borderRadius: 12, background: C.gold, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon name="check" size={14} color={C.white} />
        </div>
      ) : null}
    </div>
  );
}

export function OnbMode({ mode }) {
  const selected = mode; // Ghost Driver: de keuze is al gemaakt (solo of couple)
  return (
    <div style={onbRoot} data-testid="onb-mode">
      <div style={onbScroll}>
        <OnboardingHeader
          eyebrow="STAP 1 · WIE ZOEKT?"
          title="Zoek je alleen of samen?"
          subtitle="IMMO MATCH werkt anders dan andere apps: bij koppels kijken jullie voorkeuren, matches én agenda's als één geheel. Uniek in België."
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 6 }}>
          <ModeCard
            selected={selected === 'solo'}
            icon="user"
            title="Ik zoek alleen"
            desc="Je stelt zelf je voorkeuren in en beslist zelf. Alle Wooncoach-inzichten focussen op jouw situatie."
            testid="mode-solo"
          />
          <ModeCard
            selected={selected === 'couple'}
            icon="users"
            title="Wij zoeken onze thuis"
            desc="Nodig je partner uit. Vanaf dan swipen jullie los, maar matches ontstaan alleen als beiden ja zeggen. Ook agenda's worden gecombineerd."
            badge="POPULAIR — UNIEK IN BELGIË"
            testid="mode-couple"
          />
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', background: C.sand, padding: 10, borderRadius: 8 }}>
          <div style={{ width: 22, height: 22, borderRadius: 11, background: 'rgba(196,138,58,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon name="info" size={12} color={C.gold} />
          </div>
          <div style={{ color: C.inkMuted, fontSize: 12, flex: 1, lineHeight: '17px' }}>Je kunt dit later altijd wijzigen vanuit je profiel — geen paniek dus.</div>
        </div>
        <div style={{ marginTop: 8 }}>
          <AppButton label={selected === 'couple' ? 'Partner uitnodigen' : 'Ga verder alleen'} variant="gold" testid="mode-submit" />
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// partner.tsx — "Zoek samen naar jullie thuis." (QR + code)
// ═══════════════════════════════════════════════════════════════
function PseudoQR({ value }) {
  // Decoratieve QR-look (demo) — deterministisch patroon uit de code-string.
  const N = 21;
  const cells = [];
  let seed = 0;
  for (let i = 0; i < value.length; i++) seed = (seed * 31 + value.charCodeAt(i)) >>> 0;
  const rand = (x, y) => {
    let h = (seed ^ (x * 374761393) ^ (y * 668265263)) >>> 0;
    h = (h ^ (h >> 13)) * 1274126177 >>> 0;
    return ((h ^ (h >> 16)) % 100) < 45;
  };
  const inFinder = (x, y) =>
    (x < 7 && y < 7) || (x >= N - 7 && y < 7) || (x < 7 && y >= N - 7);
  const finderOn = (x, y) => {
    const fx = x >= N - 7 ? x - (N - 7) : x;
    const fy = y >= N - 7 ? y - (N - 7) : y;
    if (fx === 0 || fx === 6 || fy === 0 || fy === 6) return true;
    if (fx >= 2 && fx <= 4 && fy >= 2 && fy <= 4) return true;
    return false;
  };
  for (let y = 0; y < N; y++) {
    for (let x = 0; x < N; x++) {
      const on = inFinder(x, y) ? finderOn(x, y) : rand(x, y);
      cells.push(<span key={`${x}-${y}`} style={{ width: '100%', height: '100%', background: on ? C.navy : 'transparent', display: 'block' }} />);
    }
  }
  return (
    <div style={{ width: 180, height: 180, display: 'grid', gridTemplateColumns: `repeat(${N}, 1fr)`, gridTemplateRows: `repeat(${N}, 1fr)`, background: C.white }}>
      {cells}
    </div>
  );
}

export function OnbPartner() {
  const code = 'A3F9K2'; // fictieve demo-code
  return (
    <div style={onbRoot} data-testid="onb-partner">
      <div style={{ ...onbScroll, padding: '46px 24px 60px', gap: 16 }}>
        <OnboardingHeader
          title="Zoek samen naar jullie thuis."
          subtitle="Verbind je partner en ontdek alleen woningen waar jullie allebei enthousiast van worden."
        />
        <div style={{ display: 'flex', background: C.white, borderRadius: 999, padding: 4, border: `1px solid ${C.line}` }}>
          <div style={{ flex: 1, padding: '10px 0', textAlign: 'center', borderRadius: 999, background: C.navy, color: C.white, fontSize: 12.5, fontWeight: 600 }}>Uitnodigen</div>
          <div style={{ flex: 1, padding: '10px 0', textAlign: 'center', borderRadius: 999, color: C.navy, fontSize: 12.5, fontWeight: 600 }}>Code invoeren</div>
        </div>
        <div style={{ background: C.white, borderRadius: 20, padding: 24, display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center', boxShadow: sh.soft, border: `1px solid ${C.line}` }}>
          <div style={{ padding: 16, background: C.white, borderRadius: 14, border: `1px solid ${C.line}` }}>
            <PseudoQR value={`immomatch://partner/${code}`} />
          </div>
          <div style={{ color: C.inkMuted, fontSize: 12.5 }}>Jullie code</div>
          <div data-testid="invite-code" style={{ color: C.navy, fontSize: 26, fontWeight: 800, letterSpacing: 4, marginTop: -8 }}>{code}</div>
          <div style={{ width: '100%' }}>
            <AppButton label="Deel uitnodiging" variant="gold" testid="share-invite" />
          </div>
        </div>
        <div style={{ marginTop: 4 }}>
          <AppButton label="Overslaan voor nu" variant="ghost" testid="skip-partner" />
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// profile.tsx — "STAP 2 · WAT ZOEK JE?" — eerste/volgende/investering
// ═══════════════════════════════════════════════════════════════
const PROFILE_OPTIONS = [
  {
    key: 'first_home', icon: 'home', color: C.gold, pct: 2,
    labelSolo: 'Mijn eerste woning', labelCouple: 'Onze eerste woning',
    desc: 'Je koopt voor het eerst. IMMO MATCH begeleidt je met premies, starterstips en een lager registratiebelastingtarief.',
  },
  {
    key: 'next_home', icon: 'repeat', color: '#4B7BEC', pct: 12,
    labelSolo: 'Mijn volgende woning', labelCouple: 'Onze volgende woning',
    desc: 'Je hebt al een woning en zoekt de volgende stap. Focus op verkoop-impact, meerwaarde en netto woonkost.',
  },
  {
    key: 'investment', icon: 'trending-up', color: '#22C55E', pct: 12,
    labelSolo: 'Een investeringswoning', labelCouple: 'Een investeringswoning',
    desc: 'Je koopt om te verhuren of te doen renderen. Focus op huurinkomsten, cashflow en rendementsimulaties.',
  },
];

export function OnbProfile({ mode }) {
  const isCouple = mode === 'couple';
  const selected = 'first_home'; // Ghost Driver showcase: Sofie en Tom & Emma zijn starters
  return (
    <div style={onbRoot} data-testid="onb-profile">
      <div style={onbScroll}>
        <OnboardingHeader
          eyebrow="STAP 2 · WAT ZOEK JE?"
          title={isCouple ? 'Wat zoeken jullie?' : 'Wat zoek je?'}
          subtitle="Deze keuze bepaalt jouw hele woonreis: financiering, registratiebelasting, tips en partneraanbevelingen. Je kunt dit later altijd wijzigen."
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 }}>
          {PROFILE_OPTIONS.map((opt) => {
            const on = selected === opt.key;
            const label = isCouple ? opt.labelCouple : opt.labelSolo;
            return (
              <div
                key={opt.key}
                data-testid={`profile-${opt.key}`}
                style={{
                  background: on ? 'rgba(196,138,58,0.04)' : C.white, borderRadius: 14, padding: 14,
                  border: `1.5px solid ${on ? C.gold : C.line}`, display: 'flex', flexDirection: 'column', gap: 8,
                  position: 'relative', boxShadow: on ? sh.card : sh.soft,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 12,
                    background: on ? opt.color : opt.color + '20',
                    border: `1px solid ${on ? opt.color : opt.color + '55'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <Icon name={opt.icon} size={20} color={on ? C.white : opt.color} />
                  </div>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <div style={{ color: C.navy, fontSize: 15.5, fontWeight: 900, letterSpacing: -0.2 }}>{label}</div>
                    <div style={{ display: 'inline-flex', alignSelf: 'flex-start', alignItems: 'center', gap: 4, padding: '3px 7px', background: C.bgSoft, borderRadius: 999, border: `1px solid ${C.line}`, marginTop: 2 }}>
                      <Icon name="percent" size={9} color={C.inkMuted} />
                      <span style={{ color: C.inkMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.3 }}>{opt.pct}% registratiebelasting</span>
                    </div>
                  </div>
                  {on ? (
                    <div style={{ width: 26, height: 26, borderRadius: 13, background: opt.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon name="check" size={14} color={C.white} />
                    </div>
                  ) : null}
                </div>
                <div style={{ color: C.inkMuted, fontSize: 12.5, lineHeight: '17.5px', marginLeft: 56 }}>{opt.desc}</div>
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: C.sand, padding: 10, borderRadius: 8 }}>
          <div style={{ width: 20, height: 20, borderRadius: 10, background: 'rgba(196,138,58,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1, flexShrink: 0 }}>
            <Icon name="info" size={11} color={C.gold} />
          </div>
          <div style={{ color: C.inkMuted, fontSize: 11.5, flex: 1, lineHeight: '16px', fontStyle: 'italic' }}>
            Indicatieve Vlaamse registratiebelasting. Werkelijke kosten kunnen verschillen afhankelijk van regio en persoonlijke situatie.
          </div>
        </div>
        <div style={{ marginTop: 8 }}>
          <AppButton label="Ga verder" variant="gold" testid="profile-submit" />
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// type.tsx — "Wat voor thuis zoek je?" — 2×2 foto-grid + subtypes
// ═══════════════════════════════════════════════════════════════
const TYPE_PRIMARY = [
  { key: 'huis', label: 'Huis', image: 'https://images.unsplash.com/photo-1605276374104-dee2a0ed3cd6?auto=format&fit=crop&w=800&q=80' },
  { key: 'appartement', label: 'Appartement', image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=800&q=80' },
  { key: 'nieuwbouw', label: 'Nieuwbouw', image: 'https://images.unsplash.com/photo-1523217582562-09d0def993a6?auto=format&fit=crop&w=800&q=80' },
  { key: 'landelijk', label: 'Landelijk / Hoeve', image: 'https://images.unsplash.com/photo-1464146072230-91cabc968266?auto=format&fit=crop&w=800&q=80' },
];

// Subtype-catalogus voor 'huis' (publiek endpoint /property-subtypes in de app)
const HUIS_SUBTYPES = [
  { key: 'rijhuis', label: 'Rijhuis' },
  { key: 'halfopen_bebouwing', label: 'Halfopen bebouwing' },
  { key: 'open_bebouwing', label: 'Open bebouwing' },
  { key: 'bungalow', label: 'Bungalow' },
  { key: 'villa', label: 'Villa' },
  { key: 'herenhuis', label: 'Herenhuis' },
];

export function OnbType({ mode }) {
  const selected = ['huis']; // Ghost Driver showcase-seed: property_types = ['huis']
  const subtypes = mode === 'couple' ? ['halfopen_bebouwing'] : ['rijhuis'];
  return (
    <div style={onbRoot} data-testid="onb-type">
      <div style={{ ...onbScroll, padding: '46px 24px 60px', gap: 16 }}>
        <OnboardingHeader
          eyebrow="Stap 1 van 2"
          title="Wat voor thuis zoek je?"
          subtitle="Kies één of meerdere woningtypes. We gebruiken deze voorkeuren om woningen te vinden die écht bij jouw leven passen."
        />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {TYPE_PRIMARY.map((o) => {
            const on = selected.includes(o.key);
            return (
              <div
                key={o.key}
                data-testid={`type-${o.key}`}
                style={{
                  width: 'calc(50% - 5px)', aspectRatio: '3/4', borderRadius: 20, overflow: 'hidden',
                  border: `2px solid ${on ? C.gold : 'transparent'}`, background: C.navy,
                  position: 'relative', boxShadow: sh.card, boxSizing: 'border-box',
                }}
              >
                <img src={o.image} alt={o.label} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                <div style={{
                  position: 'absolute', inset: 0,
                  background: on
                    ? 'linear-gradient(180deg, rgba(16,42,67,0.10) 0%, rgba(10,26,44,0.55) 55%, rgba(10,26,44,0.92) 100%)'
                    : 'linear-gradient(180deg, rgba(10,26,44,0.05) 0%, rgba(10,26,44,0.45) 55%, rgba(10,26,44,0.88) 100%)',
                }} />
                {on ? (
                  <div style={{ position: 'absolute', top: 10, right: 10, width: 26, height: 26, borderRadius: 13, background: C.gold, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: sh.soft }}>
                    <Icon name="check" size={13} color={C.navy} />
                  </div>
                ) : null}
                <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '10px 14px 14px', background: 'rgba(10,26,44,0.35)' }}>
                  <span style={{ color: C.white, fontSize: 18, fontWeight: 800, letterSpacing: 0.2, textShadow: '0 1px 4px rgba(0,0,0,0.45)' }}>{o.label}</span>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: C.white, borderRadius: 999, padding: '12px 18px',
          border: `1px solid ${C.line}`, boxShadow: sh.soft,
        }} data-testid="type-more">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="plus-circle" size={16} color={C.navy} />
            <span style={{ color: C.navy, fontSize: 14, fontWeight: 600 }}>Meer opties</span>
          </div>
          <Icon name="chevron-down" size={16} color={C.inkFaint} />
        </div>

        {/* Sub-types van Huis — seed: rijhuis (solo) / halfopen bebouwing (Tom) */}
        <div style={{ padding: 12, borderRadius: 14, background: C.white, border: `1px solid ${C.line}`, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="git-branch" size={13} color={C.gold} />
            <span style={{ color: C.navy, fontSize: 13.5, fontWeight: 700, flex: 1 }}>Welk soort huis?</span>
            <span style={{ color: C.inkFaint, fontSize: 10, fontStyle: 'italic', letterSpacing: 0.3 }}>Optioneel</span>
          </div>
          <div style={{ color: C.inkMuted, fontSize: 12, lineHeight: '16px' }}>Kies één of meerdere sub-types die bij jou passen — dit verfijnt jouw matches.</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {HUIS_SUBTYPES.map((s) => {
              const on = subtypes.includes(s.key);
              return (
                <span
                  key={s.key}
                  data-testid={`subtype-${s.key}`}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4, padding: '7px 11px', borderRadius: 999,
                    background: on ? C.navy : C.bgSoft, border: `1px solid ${on ? C.navy : C.line}`,
                    color: on ? C.white : C.navy, fontSize: 12, fontWeight: on ? 700 : 600,
                  }}
                >
                  {on ? <Icon name="check" size={12} color={C.white} /> : null}
                  {s.label}
                </span>
              );
            })}
          </div>
        </div>

        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <AppButton label="Volgende" variant="primary" testid="type-next" />
          <div style={{ color: C.inkMuted, fontSize: 12, fontStyle: 'italic', textAlign: 'center' }}>1 type geselecteerd</div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// preferences.tsx — "Vertel ons wat voor jou belangrijk is."
// ═══════════════════════════════════════════════════════════════
const PREF_TRANSACTIONS = [
  { key: 'koop', label: 'Kopen', icon: 'key', hint: 'Eigen woning' },
  { key: 'huur', label: 'Huren', icon: 'home', hint: 'Huurwoning' },
  { key: 'beide', label: 'Nog aan het verkennen', icon: 'compass', hint: 'Bekijk kopen én huren' },
];
const PREF_PROVINCES = ['Oost-Vlaanderen', 'West-Vlaanderen', 'Antwerpen', 'Vlaams-Brabant', 'Limburg', 'Brussel', 'Wallonië'];
const PREF_EPC = ['A', 'B', 'C', 'D', 'E', 'F'];
const PREF_BEDROOMS = [1, 2, 3, 4, 5];
const PREF_POPULAR = ['Gent', 'Oudenaarde', 'Aalst', 'Antwerpen', 'Brugge', 'Leuven', 'Kortrijk', 'Mechelen', 'Hasselt'];
const PREF_WISHES_KOOP = [
  ['tuin', 'Tuin', 'sun'], ['terras', 'Terras', 'sunrise'], ['garage', 'Garage', 'square'],
  ['oprit', 'Oprit', 'corner-up-right'], ['open_keuken', 'Open keuken', 'coffee'], ['eiland', 'Kookeiland', 'grid'],
  ['bureau', 'Bureau', 'edit-3'], ['dressing', 'Dressing', 'archive'], ['berging', 'Berging', 'box'],
  ['kelder', 'Kelder', 'download'], ['zolder', 'Zolder', 'upload'], ['zwembad', 'Zwembad', 'droplet'],
  ['zuid_tuin', 'Zuidgerichte tuin', 'compass'], ['energiezuinig', 'Energiezuinig (EPC A/B)', 'zap'],
  ['zonnepanelen', 'Zonnepanelen', 'sun'], ['warmtepomp', 'Warmtepomp', 'thermometer'],
  ['instapklaar', 'Instapklaar', 'check-circle'], ['nieuwbouw', 'Nieuwbouw', 'star'],
  ['geen_reno', 'Geen renovatie nodig', 'shield'],
];

// Ghost Driver showcase-seed (auto_demo.py → _seed_ghost_driver_preferences)
const PREF_SEED = {
  solo: { minBudget: 250000, maxBudget: 350000, minSurface: 100, maxSurface: 180, bedrooms: 3, epc: 'C', regions: ['Oost-Vlaanderen'], cities: ['Gent'], wishes: ['tuin', 'energiezuinig', 'open_keuken', 'bureau'] },
  tom: { minBudget: 340000, maxBudget: 420000, minSurface: 120, maxSurface: 220, bedrooms: 3, epc: 'B', regions: ['Oost-Vlaanderen'], cities: ['Gent', 'Wondelgem'], wishes: ['tuin', 'garage', 'bureau', 'oprit'] },
  emma: { minBudget: 320000, maxBudget: 400000, minSurface: 110, maxSurface: 200, bedrooms: 3, epc: 'B', regions: ['Oost-Vlaanderen'], cities: ['Gent', 'Wondelgem'], wishes: ['tuin', 'energiezuinig', 'garage', 'ov_dichtbij'] },
};

const fmtEur = (n) => `€ ${Math.round(n).toLocaleString('nl-BE')}`;

function PrefSection({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, background: C.white, borderRadius: 20, padding: 16, border: `1px solid ${C.line}`, boxShadow: sh.soft }}>
      <div style={{ color: C.navy, fontSize: 15, fontWeight: 600 }}>{label}</div>
      {children}
    </div>
  );
}

function ReadonlyInput({ caption, value, placeholder, testid }) {
  return (
    <div style={{ flex: 1 }}>
      <div style={{ color: C.inkMuted, fontSize: 11, fontWeight: 500, letterSpacing: 1, textTransform: 'uppercase', marginTop: 6 }}>{caption}</div>
      <div
        data-testid={testid}
        style={{
          background: C.white, borderRadius: 14, border: `1px solid ${C.line}`,
          padding: '10px 12px', fontSize: 15, color: value ? C.navy : C.inkFaint, marginTop: 4,
        }}
      >{value || placeholder}</div>
    </div>
  );
}

export function OnbPrefs({ mode, step }) {
  const isCouple = mode === 'couple';
  const isEmma = isCouple && step?.id === 7;
  const seed = isCouple ? (isEmma ? PREF_SEED.emma : PREF_SEED.tom) : PREF_SEED.solo;
  const transaction = 'koop';

  return (
    <div style={{ minHeight: '100%', background: '#F1EBDF', paddingBottom: 60 }} data-testid="onb-preferences">
      <div style={{ padding: '46px 24px 60px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <OnboardingHeader
          eyebrow="Stap 4 van 4"
          title={isCouple ? 'Vertel ons wat voor jullie belangrijk is.' : 'Vertel ons wat voor jou belangrijk is.'}
          subtitle={isCouple ? 'Schuif, tik en kies. Hoe scherper jullie voorkeuren, hoe beter jullie matches.' : 'Schuif, tik en kies. Hoe scherper jouw voorkeuren, hoe beter jouw matches.'}
        />

        {/* Kopen of huren */}
        <PrefSection label="Wil je kopen of huren?">
          <div style={{ display: 'flex', gap: 8 }}>
            {PREF_TRANSACTIONS.map((t) => {
              const on = transaction === t.key;
              return (
                <div
                  key={t.key}
                  data-testid={`tx-${t.key}`}
                  style={{
                    flex: 1, padding: 10, borderRadius: 14, display: 'flex', flexDirection: 'column', gap: 4,
                    background: on ? C.navy : C.white, border: `1.5px solid ${on ? C.gold : C.line}`, minWidth: 0,
                  }}
                >
                  <div style={{ width: 28, height: 28, borderRadius: 14, background: on ? C.gold : 'rgba(16,42,67,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon name={t.icon} size={18} color={on ? C.white : C.navy} />
                  </div>
                  <div style={{ color: on ? C.white : C.navy, fontSize: 14, fontWeight: 600 }}>{t.label}</div>
                  <div style={{ color: on ? 'rgba(255,255,255,0.75)' : C.inkMuted, fontSize: 10.5, lineHeight: '14px' }}>{t.hint}</div>
                </div>
              );
            })}
          </div>
        </PrefSection>

        {/* Budget */}
        <PrefSection label="Budget">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '6px 0' }}>
            <span style={{ color: C.navy, fontSize: 18, fontWeight: 600 }}>{fmtEur(seed.minBudget)}</span>
            <span style={{ color: C.gold, fontSize: 20, fontWeight: 600 }}>—</span>
            <span style={{ color: C.navy, fontSize: 18, fontWeight: 600 }}>{fmtEur(seed.maxBudget)}</span>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <ReadonlyInput caption="Van" value={String(seed.minBudget)} testid="input-min-budget" />
            <ReadonlyInput caption="Tot" value={String(seed.maxBudget)} testid="input-max-budget" />
          </div>
          <div style={{ color: C.inkFaint, fontStyle: 'italic', fontSize: 11, marginTop: 6 }}>Vrij in te vullen — geen bovengrens.</div>
        </PrefSection>

        {/* Oppervlakte */}
        <PrefSection label="Oppervlakte">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '6px 0' }}>
            <span style={{ color: C.navy, fontSize: 18, fontWeight: 600 }}>{seed.minSurface} m²</span>
            <span style={{ color: C.gold, fontSize: 20, fontWeight: 600 }}>—</span>
            <span style={{ color: C.navy, fontSize: 18, fontWeight: 600 }}>{seed.maxSurface} m²</span>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <ReadonlyInput caption="Van" value={String(seed.minSurface)} testid="input-min-surface" />
            <ReadonlyInput caption="Tot" value={String(seed.maxSurface)} testid="input-max-surface" />
          </div>
          <div style={{ color: C.inkFaint, fontStyle: 'italic', fontSize: 11, marginTop: 6 }}>Vrij in te vullen — geen limiet.</div>
        </PrefSection>

        {/* Slaapkamers */}
        <PrefSection label="Slaapkamers (minimaal)">
          <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between' }}>
            {PREF_BEDROOMS.map((n) => {
              const on = seed.bedrooms === n;
              return (
                <div
                  key={n}
                  data-testid={`bed-${n}`}
                  style={{
                    flex: 1, height: 56, borderRadius: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: on ? C.gold : C.sand, border: `1.5px solid ${on ? C.gold : C.line}`,
                  }}
                >
                  <span style={{ color: C.navy, fontSize: 18, fontWeight: 600 }}>{n === 5 ? '5+' : n}</span>
                </div>
              );
            })}
          </div>
        </PrefSection>

        {/* Waar wil je wonen? */}
        <PrefSection label="Waar wil je wonen?">
          <div style={{ color: C.inkMuted, fontSize: 12, lineHeight: '17px', marginBottom: 10 }}>Kies één of meerdere provincies. Je kan later gemeenten fijner selecteren.</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {PREF_PROVINCES.map((r) => {
              const on = seed.regions.includes(r);
              return (
                <span
                  key={r}
                  data-testid={`prov-${r.toLowerCase().replace(/\s/g, '-')}`}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 999,
                    background: on ? C.navy : C.white, border: `1px solid ${on ? C.navy : C.line}`,
                    color: on ? C.white : C.navy, fontSize: 12, fontWeight: 600,
                  }}
                >
                  <Icon name={on ? 'check-circle' : 'map'} size={13} color={on ? C.white : C.navy} />
                  {r}
                </span>
              );
            })}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 0', marginTop: 8 }} data-testid="toggle-city-search">
            <Icon name="chevron-up" size={14} color={C.gold} />
            <span style={{ color: C.gold, fontSize: 13, fontWeight: 600 }}>Verberg specifieke gemeenten</span>
          </div>
          {seed.cities.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {seed.cities.map((city) => (
                <span key={city} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: C.navy, padding: '6px 10px', borderRadius: 999 }}>
                  <span style={{ color: C.white, fontSize: 12.5, fontWeight: 700 }}>{city}</span>
                  <Icon name="x" size={12} color={C.white} />
                </span>
              ))}
            </div>
          ) : null}
          <div style={{ color: C.inkMuted, fontSize: 11, fontWeight: 500, letterSpacing: 1, textTransform: 'uppercase', marginTop: 4 }}>Populair</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {PREF_POPULAR.map((city) => {
              const on = seed.cities.includes(city);
              return (
                <span
                  key={city}
                  data-testid={`popular-${city}`}
                  style={{
                    padding: '10px 14px', borderRadius: 999, fontSize: 12.5, fontWeight: 600,
                    background: on ? C.navy : C.sand, border: `1px solid ${on ? C.navy : C.line}`,
                    color: on ? C.white : C.navy,
                  }}
                >{city}</span>
              );
            })}
          </div>
        </PrefSection>

        {/* Max. EPC */}
        <PrefSection label="Max. EPC">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {PREF_EPC.map((e) => {
              const on = seed.epc === e;
              return (
                <span
                  key={e}
                  data-testid={`epc-${e}`}
                  style={{
                    padding: '10px 14px', borderRadius: 999, fontSize: 12.5, fontWeight: 600,
                    background: on ? C.navy : C.sand, border: `1px solid ${on ? C.navy : C.line}`,
                    color: on ? C.white : C.navy,
                  }}
                >{e}</span>
              );
            })}
          </div>
        </PrefSection>

        {/* Onmisbaar */}
        <PrefSection label="Welke elementen zijn voor jou onmisbaar?">
          <div style={{ color: C.inkMuted, fontSize: 12, lineHeight: '17px', marginBottom: 10 }}>
            Kies alles wat écht moet in jouw eigen woning. Hoe scherper, hoe beter de matches.
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
            {PREF_WISHES_KOOP.map(([key, label, icon]) => {
              const on = seed.wishes.includes(key);
              return (
                <span
                  key={key}
                  data-testid={`wish-${key}`}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 999,
                    background: on ? C.navy : C.white, border: `1px solid ${on ? C.navy : C.line}`,
                  }}
                >
                  <Icon name={icon} size={12} color={on ? C.white : C.gold} />
                  <span style={{ color: on ? C.white : C.navy, fontSize: 12, fontWeight: 600 }}>{label}</span>
                </span>
              );
            })}
          </div>
          <div style={{ color: C.inkFaint, fontStyle: 'italic', fontSize: 11, marginTop: 6 }}>
            {seed.wishes.filter((w) => PREF_WISHES_KOOP.some(([k]) => k === w)).length} onmisbaar elementen geselecteerd
          </div>
        </PrefSection>

        <div style={{ marginTop: 12 }}>
          <AppButton label="Bekijk jouw eerste woningmatch" variant="gold" testid="prefs-next" />
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// searching.tsx — "We zoeken woningen die bij jou passen…" (0→92→94→97)
// ═══════════════════════════════════════════════════════════════
const PERCENT_SEQUENCE = [92, 94, 97];

export function OnbSearching({ mode }) {
  const isCouple = mode === 'couple';
  const [displayPct, setDisplayPct] = useState(0);
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [done, setDone] = useState(false);
  const totalMatches = 12; // fictief (fallback-waarde uit de app)

  useEffect(() => {
    let currentPct = 0;
    let targetIdx = 0;
    let doneTimer = null;
    const iv = setInterval(() => {
      const target = PERCENT_SEQUENCE[targetIdx];
      if (currentPct < target) {
        currentPct += 1;
        setDisplayPct(currentPct);
      } else {
        targetIdx += 1;
        setPhaseIdx(targetIdx);
        if (targetIdx >= PERCENT_SEQUENCE.length) {
          clearInterval(iv);
          doneTimer = setTimeout(() => setDone(true), 350);
        } else {
          currentPct = target;
        }
      }
    }, 22);
    return () => { clearInterval(iv); if (doneTimer) clearTimeout(doneTimer); };
  }, []);

  return (
    <div data-testid="searching-screen" style={{ minHeight: '100%', background: `linear-gradient(180deg, ${C.navy}, ${C.navyDeep})`, position: 'relative', display: 'flex', flexDirection: 'column' }}>
      {/* subtle gouden sterren */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        {Array.from({ length: 18 }).map((_, i) => (
          <span key={i} style={{ position: 'absolute', width: 2, height: 2, background: 'rgba(196,138,58,0.6)', borderRadius: 1, left: `${(i * 7 + 5) % 100}%`, top: `${(i * 13 + 3) % 100}%` }} />
        ))}
      </div>
      <div style={{ flex: 1, padding: '46px 24px 24px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'center', position: 'relative' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 12 }}>
          <img src="/logo_official.png" alt="IMMO MATCH" style={{ width: 40, height: 40, objectFit: 'contain' }} />
          <div style={{ display: 'flex', alignItems: 'baseline', marginTop: 8 }}>
            <span style={{ color: C.white, letterSpacing: 2, fontWeight: 800, fontSize: 14 }}>IMMO</span>
            <span style={{ color: C.gold, fontStyle: 'italic', fontWeight: 400, fontSize: 14, marginLeft: 3 }}>match</span>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
          <div style={{ width: 200, height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8, position: 'relative' }}>
            <div style={{ position: 'absolute', width: 200, height: 200, borderRadius: 100, border: '2px solid rgba(196,138,58,0.35)', animation: 'gdHaloPulse 1.8s ease-in-out infinite' }} />
            <div style={{ width: 140, height: 140, borderRadius: 70, background: 'rgba(196,138,58,0.14)', border: '2px solid rgba(196,138,58,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {!done ? (
                <span style={{ color: C.gold, fontSize: 44, fontWeight: 800 }}>{displayPct}%</span>
              ) : (
                <span className="anim-pop" style={{ color: C.gold, fontSize: 56, fontWeight: 800 }}>{totalMatches}</span>
              )}
            </div>
          </div>
          {!done ? (
            <>
              <div style={{ color: C.white, fontSize: 18, fontWeight: 600, textAlign: 'center', padding: '0 16px' }}>
                {isCouple ? 'We zoeken woningen die bij jullie passen…' : 'We zoeken woningen die bij jou passen…'}
              </div>
              <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12.5, textAlign: 'center' }}>
                {phaseIdx === 0 && (isCouple ? 'Jullie voorkeuren analyseren' : 'Jouw voorkeuren analyseren')}
                {phaseIdx === 1 && 'Ideale regio\u2019s samenstellen'}
                {phaseIdx === 2 && (isCouple ? 'Gezamenlijke selectie klaarzetten' : 'Persoonlijke selectie klaarzetten')}
              </div>
            </>
          ) : (
            <>
              <div style={{ color: C.white, fontSize: 22, fontWeight: 900 }}>Klaar!</div>
              <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 14, textAlign: 'center', padding: '0 16px' }}>
                {totalMatches} potentiële woningmatches {isCouple ? 'voor jullie' : 'voor jou'} gevonden.
              </div>
            </>
          )}
        </div>

        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center', opacity: done ? 1 : 0.45, transition: 'opacity .4s ease' }}>
          <AppButton label="Begin met swipen" variant="gold" testid="searching-start-swipe" />
          <div style={{ height: 2 }} />
          <div data-testid="searching-start-explore" style={{ height: 52, borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', border: '1px solid rgba(255,255,255,0.35)' }}>
            <span style={{ color: C.white, fontSize: 15.5, fontWeight: 800 }}>Verken de app</span>
          </div>
          {!done ? <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, letterSpacing: 1, fontWeight: 500 }}>Bijna klaar…</div> : null}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// first-match.tsx — "Dit voelt als een echte match voor jou."
// ═══════════════════════════════════════════════════════════════
const FIRST_MATCH_DEMO = {
  solo: {
    img: 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=800&auto=format&fit=crop',
    city: 'Gent', region: 'Oost-Vlaanderen', title: 'Karakterhuis · Stationsstraat 14',
    price: '€ 320.000', matched: 11, total: 12,
    chips: ['Rustige zijstraat', 'Tuin aanwezig', 'Station 4 min'],
  },
  couple: {
    img: 'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?w=800&auto=format&fit=crop',
    city: 'Gent', region: 'Oost-Vlaanderen', title: 'Karakterhuis · Wondelgem',
    price: '€ 385.000', matched: 15, total: 16,
    chips: ['Tuin > 100 m²', 'Station 8 min', 'EPC B'],
  },
};

export function OnbFirstMatch({ mode }) {
  const isCouple = mode === 'couple';
  const p = FIRST_MATCH_DEMO[isCouple ? 'couple' : 'solo'];
  const pct = Math.round((p.matched / Math.max(p.total, 1)) * 100);
  return (
    <div data-testid="first-match-screen" style={{ minHeight: '100%', background: `linear-gradient(180deg, ${C.navy}, ${C.navyDeep})` }}>
      <div style={{ padding: '46px 24px 60px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 4, marginBottom: 12 }}>
          <img src="/logo_official.png" alt="IMMO MATCH" style={{ width: 30, height: 30, objectFit: 'contain' }} />
          <div style={{ display: 'flex', alignItems: 'flex-end', marginTop: 6 }}>
            <span style={{ color: C.white, letterSpacing: 2, fontWeight: 800, fontSize: 14 }}>IMMO</span>
            <span style={{ color: C.gold, fontStyle: 'italic', fontWeight: 400, fontSize: 14, marginLeft: 3 }}>match</span>
          </div>
        </div>

        <div style={{ color: C.gold, fontSize: 11, letterSpacing: 2, fontWeight: 700, textAlign: 'center' }}>
          {isCouple ? 'JULLIE EERSTE WONINGMATCH' : 'JOUW EERSTE WONINGMATCH'}
        </div>
        <div style={{ color: C.white, fontSize: 26, fontWeight: 900, textAlign: 'center', letterSpacing: -0.3 }}>
          {isCouple ? 'Dit voelt als een echte match voor jullie.' : 'Dit voelt als een echte match voor jou.'}
        </div>
        <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 14, textAlign: 'center', padding: '0 12px' }}>
          Deze woning voldoet aan <span style={{ color: C.gold, fontWeight: 700 }}>{pct}%</span> van {isCouple ? 'jullie' : 'je'} voorkeuren.
        </div>

        <div className="anim-rise" style={{ height: 340, borderRadius: 28, overflow: 'hidden', position: 'relative', boxShadow: sh.card, marginTop: 12 }}>
          <img src={p.img} alt={p.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, transparent, rgba(16,42,67,0.9))' }} />
          <div className="anim-pop" style={{ animationDelay: '.5s', position: 'absolute', top: 16, right: 16, display: 'flex', alignItems: 'center', gap: 6, background: C.gold, padding: '6px 10px', borderRadius: 999 }}>
            <Icon name="target" size={12} color={C.navy} />
            <span style={{ color: C.navy, fontSize: 11, fontWeight: 700 }}>{pct}% match</span>
          </div>
          <div style={{ position: 'absolute', bottom: 16, left: 16, right: 16, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{ color: C.goldSoft, fontSize: 12.5, letterSpacing: 1, textTransform: 'uppercase' }}>{p.city} · {p.region}</div>
            <div style={{ color: C.white, fontSize: 20, fontWeight: 600 }}>{p.title}</div>
            <div style={{ color: C.gold, fontSize: 18, fontWeight: 600, marginTop: 2 }}>{p.price}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
              <Icon name="check-circle" size={13} color={C.gold} />
              <span style={{ color: C.white, fontSize: 12.5 }}>Past bij {p.matched} van {isCouple ? 'jullie' : 'je'} {p.total} voorkeuren</span>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8, justifyContent: 'center' }}>
          {p.chips.map((chip) => (
            <span key={chip} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(196,138,58,0.35)', padding: '5px 10px', borderRadius: 999 }}>
              <Icon name="check" size={12} color={C.green} />
              <span style={{ color: C.white, fontSize: 12.5, fontWeight: 600 }}>{chip}</span>
            </span>
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
          <AppButton label="Bekijk deze woning" variant="gold" testid="first-match-open" />
          <div data-testid="first-match-swipe" style={{ height: 52, borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.35)' }}>
            <span style={{ color: C.white, fontSize: 15.5, fontWeight: 800 }}>Verder swipen</span>
          </div>
        </div>
      </div>
    </div>
  );
}
